'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Copy01Icon } from '@/components/ui/icon/glyphs';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useFundingStage } from '../shared/funding-chrome';
import { ManualTransferQr } from './manual-transfer-qr';
import { useManualTransferWallet } from './use-manual-transfer-wallet';
import './funding-manual-transfer.css';
export function FundingManualTransfer(t0) {
  const $ = _c(86);
  const {
    onBack,
    onCryptoDeposit
  } = t0;
  const t = useTranslations("onramp");
  const wallet = useManualTransferWallet();
  const [copyFailureAddress, setCopyFailureAddress] = useState(null);
  let t1;
  if ($[0] !== t) {
    t1 = t("manual_transfer");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useFundingStage(t1, false, onBack);
  let t2;
  let t3;
  if ($[2] !== wallet.network) {
    t2 = () => {
      onEvent("onramp_manual_transfer_opened", {
        platform: "web",
        surface: "manual_transfer",
        network: wallet.network
      });
    };
    t3 = [wallet.network];
    $[2] = wallet.network;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== wallet.errorCode || $[6] !== wallet.network) {
    t4 = () => {
      if (wallet.errorCode) {
        logger.warn("onramp.manual_transfer_wallet_failed", {
          platform: "web",
          surface: "manual_transfer",
          network: wallet.network,
          code: wallet.errorCode
        });
      }
    };
    t5 = [wallet.errorCode, wallet.network];
    $[5] = wallet.errorCode;
    $[6] = wallet.network;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== t || $[10] !== wallet.address || $[11] !== wallet.network) {
    t6 = async function copy() {
      if (!wallet.address) {
        return;
      }
      setCopyFailureAddress(null);
      try {
        await navigator.clipboard.writeText(wallet.address);
      } catch {
        setCopyFailureAddress(wallet.address);
        logger.warn("onramp.manual_transfer_copy_failed", {
          platform: "web",
          surface: "manual_transfer",
          network: wallet.network,
          code: "clipboard_write_failed"
        });
        return;
      }
      showToast({
        id: TOAST_IDS.WALLET_ADDRESS_COPIED,
        message: t("manual_transfer_copied"),
        variant: "success"
      });
      onEvent("onramp_manual_transfer_address_copied", {
        platform: "web",
        surface: "manual_transfer",
        network: wallet.network
      });
    };
    $[9] = t;
    $[10] = wallet.address;
    $[11] = wallet.network;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const copy = t6;
  if (wallet.status === "loading") {
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Skeleton className="my-2 h-12 w-full rounded-control" />;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== t) {
      t8 = t("manual_transfer_loading");
      $[14] = t;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t8) {
      t9 = <output aria-busy="true" className="flex flex-col gap-4">{t7}<p className="text-body text-foreground-secondary">{t8}</p></output>;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  if (!wallet.address || !wallet.qrValue) {
    let t7;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ManualArtwork src="/assets/onramp/status-offline.png" size="size-26" />;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    let t8;
    if ($[19] !== t) {
      t8 = t("manual_transfer_unavailable");
      $[19] = t;
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    let t9;
    if ($[21] !== t8) {
      t9 = <h3 className="text-lg font-semibold">{t8}</h3>;
      $[21] = t8;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    let t10;
    if ($[23] !== t) {
      t10 = t("manual_transfer_retry");
      $[23] = t;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== t10) {
      t11 = <p className="text-body text-foreground-secondary">{t10}</p>;
      $[25] = t10;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t12;
    if ($[27] !== wallet) {
      t12 = () => {
        wallet.retry().catch(() => logger.warn("onramp.manual_transfer_wallet_failed", {
          platform: "web",
          surface: "manual_transfer",
          network: wallet.network,
          code: "wallet_retry_failed"
        }));
      };
      $[27] = wallet;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== t) {
      t13 = t("retry");
      $[29] = t;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== t12 || $[32] !== t13) {
      t14 = <AnimatedButton className="h-11 w-full" onClick={t12}>{t13}</AnimatedButton>;
      $[31] = t12;
      $[32] = t13;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== t11 || $[35] !== t14 || $[36] !== t9) {
      t15 = <div className="flex flex-col gap-4">{t7}{t9}{t11}{t14}</div>;
      $[34] = t11;
      $[35] = t14;
      $[36] = t9;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    return t15;
  }
  let t7;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ManualArtwork src="/assets/onramp/usdc.svg" size="size-7" />;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  let t8;
  if ($[39] !== t) {
    t8 = t("manual_transfer_asset");
    $[39] = t;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== t8) {
    t9 = <div className="flex items-center justify-center gap-2">{t7}<strong className="text-body">{t8}</strong></div>;
    $[41] = t8;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== t) {
    t10 = t("manual_transfer_asset");
    $[43] = t;
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== wallet.qrValue) {
    t11 = <ManualTransferQr value={wallet.qrValue} />;
    $[45] = wallet.qrValue;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  let t12;
  if ($[47] !== t10 || $[48] !== t11) {
    t12 = <div className="funding-manual-qr relative isolate flex min-h-68 items-center justify-center overflow-hidden rounded-control bg-background-tertiary py-6"><div role="img" className="relative overflow-hidden rounded-control bg-white shadow-lg" aria-label={t10}>{t11}</div></div>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== t) {
    t13 = t("manual_transfer_address");
    $[50] = t;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t13) {
    t14 = <p className="text-caption text-foreground-secondary">{t13}</p>;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  let t15;
  if ($[54] !== wallet.address) {
    t15 = <code className="break-all font-mono text-xs">{wallet.address}</code>;
    $[54] = wallet.address;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== copy) {
    t16 = () => {
      copy();
    };
    $[56] = copy;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== t) {
    t17 = t("manual_transfer_copy");
    $[58] = t;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== t16 || $[61] !== t17) {
    t18 = <AnimatedButton variant="outline" prefix={Copy01Icon} className="h-11 w-full" onClick={t16}>{t17}</AnimatedButton>;
    $[60] = t16;
    $[61] = t17;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== copyFailureAddress || $[64] !== t || $[65] !== wallet.address) {
    t19 = copyFailureAddress === wallet.address ? <p role="alert" className="text-caption text-feedback-error-content">{t("manual_transfer_copy_failed")}</p> : null;
    $[63] = copyFailureAddress;
    $[64] = t;
    $[65] = wallet.address;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== t14 || $[68] !== t15 || $[69] !== t18 || $[70] !== t19) {
    t20 = <div className="flex flex-col gap-2.5 text-center">{t14}{t15}{t18}{t19}</div>;
    $[67] = t14;
    $[68] = t15;
    $[69] = t18;
    $[70] = t19;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== t) {
    t21 = t("manual_transfer_warning");
    $[72] = t;
    $[73] = t21;
  } else {
    t21 = $[73];
  }
  let t22;
  if ($[74] !== t21) {
    t22 = <p className="text-center text-xs leading-4.5 text-foreground-secondary">{t21}</p>;
    $[74] = t21;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== onCryptoDeposit || $[77] !== t || $[78] !== wallet.network) {
    t23 = onCryptoDeposit ? <AnimatedButton variant="tertiary" className="w-full underline underline-offset-3" onClick={() => {
      onEvent("onramp_manual_transfer_alternate_crypto_selected", {
        platform: "web",
        surface: "manual_transfer",
        network: wallet.network
      });
      onCryptoDeposit();
    }}>{t("manual_transfer_alternate")}</AnimatedButton> : null;
    $[76] = onCryptoDeposit;
    $[77] = t;
    $[78] = wallet.network;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[80] !== t12 || $[81] !== t20 || $[82] !== t22 || $[83] !== t23 || $[84] !== t9) {
    t24 = <div className="flex flex-col gap-5">{t9}{t12}{t20}{t22}{t23}</div>;
    $[80] = t12;
    $[81] = t20;
    $[82] = t22;
    $[83] = t23;
    $[84] = t9;
    $[85] = t24;
  } else {
    t24 = $[85];
  }
  return t24;
}
function ManualArtwork(t0) {
  const $ = _c(3);
  const {
    src,
    size
  } = t0;
  let t1;
  if ($[0] !== size || $[1] !== src) {
    t1 = <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" className="bg-transparent" src={src} size={size} alt="" />;
    $[0] = size;
    $[1] = src;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}